import { Webline } from 'components/Layout/Webline/Webline';
import { filterStoreGroups } from 'components/Pages/Stores/helpers/filterStores';
import { StoreGroupSection } from 'components/Pages/Stores/StoreGroupSection';
import { StoreLocatorMap } from 'components/Pages/Stores/StoreLocatorMap';
import { useStoreTerritorialUnits } from 'connectors/stores/StoreLocator';
import { MapProvider } from 'context/Map/MapProvider';
import { getStoreByUuid } from 'helpers/searchedStore';
import { EMPTY_SERVICE_FILTERS, useStoreFilters } from 'hooks/useStoreFilters';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import {
    StoreLocatorStoreType,
    StoreLocatorStoreTypeGroups,
    StoreServiceFiltersType,
    StoreServiceIconsType,
} from 'types/store';
import { shallow } from 'zustand/shallow';

const TEST_IDENTIFIER = 'page-stores';

const getDistanceFromPosition = (store: StoreLocatorStoreType | null, latitude: number, longitude: number): number => {
    if (store === null || store.locationLatitude === null || store.locationLongitude === null) {
        return Infinity;
    }

    const latitudeDifference = ((store.locationLatitude - latitude) * Math.PI) / 180;
    const longitudeDifference = ((store.locationLongitude - longitude) * Math.PI) / 180;
    const latitudeRadians = (latitude * Math.PI) / 180;
    const storeLatitudeRadians = (store.locationLatitude * Math.PI) / 180;
    const haversine =
        Math.sin(latitudeDifference / 2) ** 2 +
        Math.cos(latitudeRadians) * Math.cos(storeLatitudeRadians) * Math.sin(longitudeDifference / 2) ** 2;

    return 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

type StoresProps = {
    isLoading: boolean;
    serviceIcons: StoreServiceIconsType;
    stores: StoreLocatorStoreTypeGroups;
};

const Stores: FC<StoresProps> = memo(({ isLoading, serviceIcons, stores }) => {
    const { t } = useTranslation();
    const { defaultStoreUuid, updateDefaultStoreUuidState, updateUserState } = usePersistStore(
        (s) => ({
            defaultStoreUuid: s.defaultStoreUuid,
            updateDefaultStoreUuidState: s.updateDefaultStoreUuidState,
            updateUserState: s.updateUserState,
        }),
        shallow,
    );
    const allStores = useMemo(() => [...stores.tescoma, ...stores.partner], [stores]);
    const selectedStore = useMemo(() => getStoreByUuid(allStores, defaultStoreUuid), [allStores, defaultStoreUuid]);
    const [geolocatedStoreUuid, setGeolocatedStoreUuid] = useState<string | null>(null);
    const [geolocationRequestId, setGeolocationRequestId] = useState(0);
    const clearGeolocatedStore = useCallback(() => setGeolocatedStoreUuid(null), []);
    const {
        changeDistrict,
        changeRegion,
        changeSearch,
        changeServiceFilter,
        debouncedSearchValue,
        loadTerritorialUnits,
        resetFilters,
        searchValue,
        selectedDistrict,
        selectedRegion,
        serviceFilters,
        shouldLoadTerritorialUnits,
    } = useStoreFilters(clearGeolocatedStore);
    const { territorialUnits, isLoading: areTerritorialUnitsLoading } =
        useStoreTerritorialUnits(shouldLoadTerritorialUnits);
    const availableServices = useMemo(() => {
        const availableServices: StoreServiceFiltersType = { ...EMPTY_SERVICE_FILTERS };

        for (const store of allStores) {
            availableServices.acceptGiftCoupon ||= store.acceptGiftCoupon;
            availableServices.hasLoyaltyProgram ||= store.hasLoyaltyProgram;
            availableServices.isPickupPoint ||= store.isPickupPoint;
        }

        return availableServices;
    }, [allStores]);
    const filteredStores = useMemo(
        () => filterStoreGroups(stores, debouncedSearchValue, selectedRegion, selectedDistrict, serviceFilters),
        [debouncedSearchValue, selectedDistrict, selectedRegion, serviceFilters, stores],
    );
    const displayedStoresList = useMemo(() => [...filteredStores.tescoma, ...filteredStores.partner], [filteredStores]);
    const geolocateHandler = useCallback(
        (latitude: number, longitude: number) => {
            const closestStore = allStores.reduce<StoreLocatorStoreType | null>((currentClosestStore, store) => {
                if (
                    getDistanceFromPosition(store, latitude, longitude) >=
                    getDistanceFromPosition(currentClosestStore, latitude, longitude)
                ) {
                    return currentClosestStore;
                }

                return store;
            }, null);

            resetFilters();
            setGeolocatedStoreUuid(closestStore?.uuid ?? null);
            setGeolocationRequestId((currentRequestId) => currentRequestId + 1);
        },
        [allStores, resetFilters],
    );

    const setDefaultStoreHandler = useCallback(
        (store: StoreLocatorStoreType) => {
            if (store.uuid === defaultStoreUuid) {
                updateDefaultStoreUuidState(null);
                updateUserState({ selectedStoreUuid: null });

                return;
            }

            updateDefaultStoreUuidState(store.uuid);
            updateUserState({ selectedStoreUuid: store.uuid });
        },
        [defaultStoreUuid, updateDefaultStoreUuidState, updateUserState],
    );

    return (
        <Webline data-testid={TEST_IDENTIFIER}>
            <h1 data-testid="basic-heading-h1">{t('TESCOMA places of sale')}</h1>
            <section className="user-text mb-7 text-body leading-6 vl:mb-8 vl:text-h6 vl:leading-8 [&>strong]:font-bold">
                <Trans
                    components={{ 0: <a href="/" tabIndex={0} />, 1: <strong /> }}
                    i18nKey="Find a store in a place that interests you"
                />
            </section>
            <MapProvider>
                {(provider: string | undefined) => (
                    <StoreLocatorMap
                        areTerritorialUnitsLoading={areTerritorialUnitsLoading}
                        availableServices={availableServices}
                        geolocatedStoreUuid={geolocatedStoreUuid}
                        geolocationRequestId={geolocationRequestId}
                        isLoading={isLoading}
                        mapProvider={provider}
                        searchValue={searchValue}
                        selectedDistrict={selectedDistrict}
                        selectedRegion={selectedRegion}
                        serviceFilters={serviceFilters}
                        serviceIcons={serviceIcons}
                        stores={displayedStoresList}
                        territorialUnits={territorialUnits}
                        onDistrictChange={changeDistrict}
                        onGeolocate={geolocateHandler}
                        onRegionChange={changeRegion}
                        onSearchChange={changeSearch}
                        onServiceFilterChange={changeServiceFilter}
                        onTerritorialSelectorFocus={loadTerritorialUnits}
                    />
                )}
            </MapProvider>
            {selectedStore !== null && (
                <div className="-mx-5 mb-[30px] rounded-md bg-grayLight p-5 [@media(min-width:1400px)]:-mx-20 [@media(min-width:1400px)]:mb-[60px] [@media(min-width:1400px)]:px-20 [@media(min-width:1400px)]:pb-[30px] [@media(min-width:1400px)]:pt-[50px]">
                    <StoreGroupSection
                        removeBottomMargin
                        description={t('My store description')}
                        selectedStoreUuid={selectedStore.uuid}
                        serviceIcons={serviceIcons}
                        stores={[selectedStore]}
                        title={t('My store')}
                        onSelectStore={setDefaultStoreHandler}
                    />
                </div>
            )}

            {filteredStores.tescoma.length !== 0 && (
                <StoreGroupSection
                    description={t('TESCOMA sales centers description')}
                    selectedStoreUuid={selectedStore?.uuid ?? null}
                    serviceIcons={serviceIcons}
                    stores={filteredStores.tescoma}
                    title={t('TESCOMA sales centers')}
                    onSelectStore={setDefaultStoreHandler}
                />
            )}

            {filteredStores.partner.length !== 0 && (
                <StoreGroupSection
                    description={t('TESCOMA partner stores description')}
                    selectedStoreUuid={selectedStore?.uuid ?? null}
                    serviceIcons={serviceIcons}
                    stores={filteredStores.partner}
                    title={t('TESCOMA partner stores')}
                    onSelectStore={setDefaultStoreHandler}
                />
            )}
        </Webline>
    );
});

Stores.displayName = 'Stores';

export default Stores;
